import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { LuClock, LuCircleX, LuLogOut, LuMailCheck } from "react-icons/lu";
import { useAuth } from "../context/AuthContext";

export default function PendingApproval() {
  const { user, userStatus, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { navigate("/login", { replace: true }); return; }
    // onSnapshot in AuthContext fires when admin flips status — redirect immediately
    if (userStatus === "approved") { navigate("/dashboard", { replace: true }); }
  }, [user, userStatus, navigate]);

  const isRejected = userStatus === "rejected";

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-[80vh] bg-background flex items-center justify-center px-6 py-16">
      <Helmet>
        <title>
          {isRejected ? "Application Declined" : "Pending Approval"} | Khoja Distributions
        </title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="max-w-md w-full">
        {/* Icon */}
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 ${
          isRejected
            ? "bg-red-100 dark:bg-red-950/30"
            : "bg-amber-100 dark:bg-amber-950/30"
        }`}>
          {isRejected
            ? <LuCircleX  size={30} className="text-red-500" />
            : <LuClock    size={30} className="text-amber-500" />
          }
        </div>

        {/* Heading */}
        <h1 className="text-2xl font-black text-foreground tracking-tight text-center mb-3">
          {isRejected ? "Application Declined" : "Pending Admin Approval"}
        </h1>

        {/* Body */}
        <p className="text-[var(--color-muted-foreground)] text-sm leading-relaxed text-center mb-6">
          {isRejected
            ? "Your wholesale application has been reviewed and could not be approved at this time. Please reach out to us if you have questions or would like to reapply."
            : "Your application has been received and is under review. You'll gain access to your account as soon as an admin approves your submitted documents."
          }
        </p>

        {/* Live-update notice (pending only) */}
        {!isRejected && (
          <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 rounded-xl px-4 py-3 mb-8">
            <LuMailCheck size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
              This page will automatically redirect you to your dashboard the moment your account is approved — no refresh needed.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3">
          {isRejected && (
            <Link
              to="/contact"
              className="w-full text-center bg-brand-navy hover:bg-brand-amber transition-colors text-white font-bold py-3 rounded-xl text-sm"
            >
              Contact Us
            </Link>
          )}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-[var(--color-muted-foreground)] border border-border hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
          >
            <LuLogOut size={15} />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}
